import React, {
	useEffect,
	useRef,
	useMemo,
	useState,
} from 'react';
import {
	Empty,
	notification,
	Tabs,
	Typography,
	Upload,
} from 'antd';
import { RcFile, UploadFile } from 'antd/lib/upload';
import classNames from 'classnames';
import {
	PlusOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { useDropArea } from 'react-use';
import Button from '@client/components/Button';
import FileViewer from '@client/components/FileViewer';
import type { FileUploadProps, Upload as UploadedFile } from '@client/utils/hooks/useFileUpload';
import styles from './styles/InvoiceAttachment.module.css';

type InvoiceAttachmentProps = {
	files: UploadedFile[];
	uploadProps: FileUploadProps;
	onRemoveFile: (uid: string) => void;
	attachmentTypeLabel?: string;
	disabled?: boolean;
};

const InvoiceAttachment = ({
	files,
	uploadProps,
	onRemoveFile,
	attachmentTypeLabel = 'invoice',
	disabled = false,
}: InvoiceAttachmentProps) => {
	const showNotAFileNotication = (text: string) => notification.info({
		message: 'Cannot upload the dropped item',
		description: `"${text}" is not a file`,
	});

	const [containerBond, dropState] = useDropArea();
	const [overlayBond, overlayDropState] = useDropArea({
		onFiles: (newFiles) => newFiles.forEach((f) => {
			const file = f as RcFile;
			file.uid = `${f.name}-${f.lastModified}`;

			uploadProps.beforeUpload(file);
		}),
		onUri: showNotAFileNotication,
		onText: showNotAFileNotication,
	});

	const [shownFile, setShownFile] = useState<string | null>(files[0] != null ? files[0].file.uid : null);

	// Used to keep track of previous files
	const oldFiles = useRef(files);

	// When new files are added, go to the first newly added one
	useEffect(() => {
		if (disabled) {
			return;
		}

		if (files === oldFiles.current) {
			return;
		}

		const newFiles = files.filter((f) => !oldFiles.current.includes(f));

		// Update old files value
		oldFiles.current = files;

		if (newFiles.length > 0) {
			setShownFile(newFiles[0].file.uid);

			return;
		}

		// If no new files were added, maybe a file was deleted
		// If currently shown file was deleted, show first file
		if (
			files.find((f) => f.file.uid === shownFile) == null &&
			files.length > 0
		) {
			setShownFile(files[0].file.uid);
		}
	}, [files, shownFile]);

	const emptyContent = useMemo(() => {
		if (disabled) {
			return <Empty />;
		}

		return (
			<div className={styles.attachTitle}>
				<Typography.Title level={3}>
					Attach file(s)
				</Typography.Title>
				<Typography.Text>
					If you have a PDF file (or similar) with the
					{' '}
					{attachmentTypeLabel}
					<br />
					information, this is a good place to keep it.
				</Typography.Text>
				<div className={styles.uploadWrapper}>
					<div className={styles.uploadButtons}>
						<Upload
							name="attachments"
							fileList={files as unknown as UploadFile[]}
							showUploadList={false}
							multiple
							disabled={disabled}
							{...uploadProps}
						>
							{!disabled && (
								<Button
									type="primary"
									icon={(<UploadOutlined />)}
								>
									Browse files
								</Button>
							)}
						</Upload>
					</div>
					<br />
					<em>or drop files here to upload</em>
				</div>
			</div>
		);
	}, [files, disabled, attachmentTypeLabel]);

	const dropping = dropState.over || overlayDropState.over;

	const changeTab = (newTab: string) => {
		if (newTab === 'addNew') {
			return;
		}

		setShownFile(newTab);
	};

	const editTabs = (
		tab: React.MouseEvent | React.KeyboardEvent | string,
		event: 'add' | 'remove',
	) => {
		if (event === 'remove' && typeof tab === 'string') {
			onRemoveFile(tab);
		}
	};

	return (
		<div
			{...containerBond}
			className={classNames(styles.container, {
				[styles.dropping]: dropping,
			})}
		>
			<div
				className={styles.dropOverlay}
				{...overlayBond}
			>
				Drop to upload
			</div>
			{files.length === 0 ?
				emptyContent :
				(
					<Tabs
						className={styles.tabs}
						activeKey={shownFile ?? undefined}
						onChange={changeTab}
						type="editable-card"
						onEdit={editTabs}
						hideAdd
						items={[
							...files.map(({
								file,
								url,
								deleteable = true,
								label,
							}) => ({
								key: file.uid,
								label: label ?? file.name,
								className: classNames(styles.fileViewerWrapper, {
									[styles.notPdf]: file.type !== 'application/pdf',
								}),
								closable: deleteable,
								children: (
									<FileViewer
										url={url}
										type={file.type}
									/>
								),
							})),
							...(!disabled ? [
								{
									key: 'addNew',
									label: (
										<Upload
											className={styles.uploadTab}
											name="attachments"
											fileList={files as unknown as UploadFile[]}
											showUploadList={false}
											multiple
											disabled={disabled}
											{...uploadProps}
										>
											<PlusOutlined className={styles.addIcon} />
										</Upload>
									),
									closable: false,
								},
							] : [])
						]}
					/>
				)}
		</div>
	);
};

export default InvoiceAttachment;
